import React, { useRef, useCallback } from 'react'
import { TextField } from '@mui/material'
import { Controller } from 'react-hook-form'
import { useMobile } from '../../../hooks/useMobile'
import { MobileFormFieldProps } from './types'

/**
 * MobileFormField - Mobile-optimized text field with auto-scroll
 *
 * Features:
 * - Auto-scrolls to input when keyboard appears (mobile only)
 * - Minimum 44px touch target height
 * - 16px font size to prevent iOS zoom
 * - Optimized keyboard types (email, tel, number)
 * - Full react-hook-form integration
 * - Responsive styling (mobile vs desktop)
 *
 * @example
 * ```tsx
 * const { control } = useForm()
 *
 * <MobileFormField
 *   name="email"
 *   control={control}
 *   label="Email Address"
 *   type="email"
 *   rules={{ required: 'Email is required' }}
 * />
 * ```
 */
export const MobileFormField: React.FC<MobileFormFieldProps> = ({
  name,
  control,
  rules,
  autoScroll = true,
  scrollDelay = 300,
  keyboardHeight = 300,
  inputMode,
  type,
  onFocus,
  sx,
  ...textFieldProps
}) => {
  const { isMobile } = useMobile()
  const fieldRef = useRef<HTMLDivElement>(null)

  /**
   * Handle focus event with auto-scroll on mobile
   * Waits for keyboard to appear before scrolling
   */
  const handleFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      // Call original onFocus handler if provided
      onFocus?.(event)

      // Skip auto-scroll if disabled or not mobile
      if (!isMobile || !autoScroll || !fieldRef.current) return

      // Wait for keyboard to appear
      setTimeout(() => {
        if (!fieldRef.current) return

        const rect = fieldRef.current.getBoundingClientRect()
        const viewportHeight = window.innerHeight

        // Calculate target scroll position
        // Position input in the center of the visible area above keyboard
        const targetPosition = rect.top - (viewportHeight - keyboardHeight) / 2

        window.scrollBy({
          top: targetPosition,
          behavior: 'smooth',
        })
      }, scrollDelay)
    },
    [isMobile, autoScroll, scrollDelay, keyboardHeight, onFocus]
  )

  /**
   * Determine optimal inputMode based on field type
   * Optimizes mobile keyboard for different input types
   */
  const getInputMode = (): React.HTMLAttributes<HTMLInputElement>['inputMode'] => {
    // Use explicit inputMode if provided
    if (inputMode) return inputMode

    // Auto-detect from type
    switch (type) {
      case 'number':
        return 'decimal'
      case 'tel':
        return 'tel'
      case 'email':
        return 'email'
      case 'url':
        return 'url'
      case 'search':
        return 'search'
      default:
        return 'text'
    }
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          {...textFieldProps}
          ref={fieldRef}
          type={type}
          onFocus={handleFocus}
          error={!!error}
          helperText={error?.message || textFieldProps.helperText}
          inputProps={{
            ...textFieldProps.inputProps,
            inputMode: getInputMode(),
          }}
          sx={{
            // Mobile-specific styling
            ...(isMobile && {
              '& .MuiInputBase-root': {
                minHeight: 44, // Touch target size (iOS guideline)
                fontSize: 16, // Prevent iOS auto-zoom on focus
              },
            }),
            // Merge custom sx prop
            ...sx,
          }}
        />
      )}
    />
  )
}

// Display name for debugging
MobileFormField.displayName = 'MobileFormField'
